import axios, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useRef, useEffect } from 'react';
import type { InternalAxiosRequestConfig } from 'axios';
import { useAuth } from './authProvider';
import { useImpersonation } from './impersonationProvider';
import { API_BASE_URL } from '@/services/api/baseUrl';

// Set on a request that has already been retried after a 401, so it isn't
// retried again and keeps the token it was retried with.
type RetriedConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

type ApiContextValue = {
  instance: AxiosInstance;
};
const ApiContext = createContext<ApiContextValue | null>(null);

export const ApiProvider = (props: { children?: React.ReactNode }) => {
  const { getAccessToken, refreshAccessToken, logout } = useAuth();
  const { impersonateUserId } = useImpersonation();
  const impersonateRef = useRef(impersonateUserId);
  useEffect(() => {
    impersonateRef.current = impersonateUserId;
  }, [impersonateUserId]);

  const instance = useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE_URL,
    });
    instance.interceptors.request.use(
      async (config: RetriedConfig) => {
        if (!config._authRetried) {
          const token = await getAccessToken();
          config.headers.set('Authorization', `Bearer ${token}`);
        }
        if (impersonateRef.current) {
          config.headers.set('X-Impersonate-User-Id', impersonateRef.current);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    // The stored token can look valid here yet be rejected by the API (revoked,
    // or the user's account changed). Refresh and retry once; if that fails,
    // sign out so ProtectedRoute sends the user to /login instead of leaving
    // every page showing empty data.
    instance.interceptors.response.use(undefined, async (error) => {
      const config = error?.config as RetriedConfig | undefined;
      if (error?.response?.status !== 401 || !config) return Promise.reject(error);
      if (!config._authRetried) {
        config._authRetried = true;
        try {
          const token = await refreshAccessToken();
          config.headers.set('Authorization', `Bearer ${token}`);
          return instance(config);
        } catch {
          // Fall through to signing out.
        }
      }
      logout();
      return Promise.reject(error);
    });
    return instance;
  }, [getAccessToken, refreshAccessToken, logout]);

  return <ApiContext.Provider {...props} value={{ instance }} />;
};

export const useApi = () => {
  const context = useContext(ApiContext);

  if (!context) {
    throw new Error('API is not provided');
  }
  return context.instance;
};
